import React, { useState, forwardRef, useRef, useEffect } from 'react';
import './Input.css';
import { FaEye, FaEyeSlash, FaCalendarAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Input = forwardRef(({
  type = 'text',
  label,
  name,
  id,
  placeholder,
  value,
  checked,
  onChange,
  error,
  required = false,
  disabled = false,
  options = [],
  rows = 4,
  icon,
  showPasswordToggle = true,
  className = '',
  inputClassName = '',
  children,
  ...rest
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const containerRef = useRef(null);
  const inputId = id || name || `input-${Math.random().toString(36).substring(2, 9)}`;
  const isCheckboxOrRadio = type === 'checkbox' || type === 'radio';
  const isPassword = type === 'password';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const togglePasswordVisibility = (e) => {
    e.preventDefault();
    setShowPassword(!showPassword);
  };

  const handleDateSelect = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const formatted = `${yyyy}-${mm}-${dd}`;

    if (onChange) {
      onChange({
        target: {
          name,
          value: formatted,
          type: 'date'
        }
      });
    }
    setShowCalendar(false);
  };

  const handlePrevMonth = (e) => {
    e.preventDefault();
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = (e) => {
    e.preventDefault();
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const selectToday = (e) => {
    e.preventDefault();
    const today = new Date();
    setCurrentMonth(today);
    handleDateSelect(today);
  };

  const clearDate = (e) => {
    e.preventDefault();
    if (onChange) {
      onChange({
        target: {
          name,
          value: '',
          type: 'date'
        }
      });
    }
    setShowCalendar(false);
  };

  const parsedValue = value ? new Date(value) : null;
  const isValidDate = parsedValue instanceof Date && !isNaN(parsedValue);
  const activeDate = isValidDate ? parsedValue : null;

  const displayValue = activeDate
    ? activeDate.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';

  const inputType = isPassword && showPassword ? 'text' : type;

  const renderLabel = () => {
    if (!label) return null;
    return (
      <label htmlFor={inputId} className="custom-input-label">
        {label}
        {required && <span className="custom-input-required">*</span>}
      </label>
    );
  };

  const renderCalendar = () => {
    if (!showCalendar) return null;

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDayIndex = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const totalDaysPrev = new Date(year, month, 0).getDate();

    const days = [];

    for (let i = firstDayIndex - 1; i >= 0; i--) {
      days.push({
        day: totalDaysPrev - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, totalDaysPrev - i)
      });
    }

    for (let i = 1; i <= totalDays; i++) {
      days.push({
        day: i,
        isCurrentMonth: true,
        date: new Date(year, month, i)
      });
    }

    const remainingSlots = 42 - days.length;
    for (let i = 1; i <= remainingSlots; i++) {
      days.push({
        day: i,
        isCurrentMonth: false,
        date: new Date(year, month + 1, i)
      });
    }

    const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    return (
      <div className="custom-calendar-popup">
        <div className="custom-calendar-header">
          <button type="button" className="custom-calendar-nav-btn" onClick={handlePrevMonth}>
            <FaChevronLeft />
          </button>
          <span className="custom-calendar-title">
            {currentMonth.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
          </span>
          <button type="button" className="custom-calendar-nav-btn" onClick={handleNextMonth}>
            <FaChevronRight />
          </button>
        </div>
        <div className="custom-calendar-grid">
          {weekDays.map(wd => (
            <div key={wd} className="custom-calendar-weekday">{wd}</div>
          ))}
          {days.map((item, index) => {
            const isToday = new Date().toDateString() === item.date.toDateString();
            const isSelected = activeDate && activeDate.toDateString() === item.date.toDateString();
            const dayClass = `custom-calendar-day ${
              item.isCurrentMonth ? '' : 'outside-month'
            } ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`;

            return (
              <button
                key={index}
                type="button"
                className={dayClass}
                onClick={() => {
                  const selectYear = item.date.getFullYear();
                  const selectMonth = item.date.getMonth();
                  const selectDayVal = item.date.getDate();
                  const finalDate = new Date(selectYear, selectMonth, selectDayVal);
                  handleDateSelect(finalDate);
                }}
              >
                {item.day}
              </button>
            );
          })}
        </div>
        <div className="custom-calendar-footer">
          <button type="button" className="custom-calendar-footer-btn clear" onClick={clearDate}>
            Clear
          </button>
          <button type="button" className="custom-calendar-footer-btn today" onClick={selectToday}>
            Today
          </button>
        </div>
      </div>
    );
  };

  const renderInputControl = () => {
    const commonProps = {
      ref,
      id: inputId,
      name,
      disabled,
      required,
      className: `custom-input-field ${error ? 'has-error' : ''} ${icon ? 'with-icon' : ''} ${
        isPassword && showPasswordToggle ? 'with-suffix' : ''
      } ${inputClassName}`,
      onChange,
      value,
      placeholder,
      ...rest,
    };

    if (type === 'textarea') {
      return (
        <textarea
          rows={rows}
          {...commonProps}
        />
      );
    }

    if (type === 'select') {
      return (
        <select {...commonProps}>
          {placeholder && <option value="" disabled>{placeholder}</option>}
          {children ? children : options.map((opt, index) => {
            const optVal = typeof opt === 'object' ? opt.value : opt;
            const optLabel = typeof opt === 'object' ? opt.label : opt;
            return (
              <option key={index} value={optVal}>
                {optLabel}
              </option>
            );
          })}
        </select>
      );
    }

    if (isCheckboxOrRadio) {
      const checkboxProps = {
        type,
        className: `custom-input-control-row ${inputClassName}`,
        ref,
        id: inputId,
        name,
        disabled,
        required,
        onChange,
        value,
        ...rest,
      };

      if (checked !== undefined) {
        checkboxProps.checked = checked;
      }

      return <input {...checkboxProps} />;
    }

    if (type === 'date') {
      return (
        <div className="custom-input-date-container" ref={containerRef}>
          <input
            type="text"
            readOnly
            onClick={() => !disabled && setShowCalendar(!showCalendar)}
            value={displayValue}
            placeholder={placeholder || 'Select Date'}
            disabled={disabled}
            required={required}
            className={`custom-input-field ${error ? 'has-error' : ''} with-suffix ${inputClassName}`}
            {...rest}
          />
          <button
            type="button"
            className="custom-input-icon-right"
            onClick={() => !disabled && setShowCalendar(!showCalendar)}
            disabled={disabled}
          >
            <FaCalendarAlt />
          </button>
          {renderCalendar()}
        </div>
      );
    }

    return (
      <input
        type={inputType}
        value={value}
        {...commonProps}
      />
    );
  };

  const groupClassName = isCheckboxOrRadio
    ? `custom-input-group custom-input-group-row ${className}`
    : `custom-input-group ${className}`;

  return (
    <div className={groupClassName}>
      {isCheckboxOrRadio ? (
        <>
          {renderInputControl()}
          {renderLabel()}
        </>
      ) : (
        <>
          {renderLabel()}
          <div className="custom-input-wrapper">
            {icon && (
              <span className="custom-input-icon-left">{icon}</span>
            )}
            {renderInputControl()}
            {isPassword && showPasswordToggle && (
              <button
                type="button"
                className="custom-input-icon-right"
                onClick={togglePasswordVisibility}
                tabIndex="-1"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            )}
          </div>
        </>
      )}
      {error && <span className="custom-input-error-msg">{error}</span>}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;